import { useState } from "react";
import type { QuestionSettings } from "@/types/question";
import { QuestionType } from "@/types/question";
import { checklistQuestionsService } from "../services";
import type { ChecklistQuestion } from "../types/questions-types";
import type {
	QuestionOption,
	QuestionTypeOrChecklist,
} from "./useQuestionForm";
import { useCreateQuestion } from "./useQuestionMutations";

export const useQuestionSubmit = () => {
	const [error, setError] = useState<string | null>(null);
	const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
	const [success, setSuccess] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const createQuestionMutation = useCreateQuestion();

	const handleSave = async (
		title: string,
		selectedType: QuestionTypeOrChecklist | null,
		settings: QuestionSettings,
		options: QuestionOption[],
		description?: string
	) => {
		if (!title.trim()) {
			setError("O título da pergunta é obrigatório.");
			return;
		}

		if (!selectedType) {
			setError("Selecione um tipo de pergunta.");
			return;
		}

		if (
			(selectedType === QuestionType.Option || selectedType === 999) &&
			options.length === 0
		) {
			setError("Adicione pelo menos uma opção de resposta.");
			return;
		}

		if (selectedType === QuestionType.Option) {
			const hasEmptyOption = options.some((option) => !option.text.trim());
			if (hasEmptyOption) {
				setError("Preencha o texto de todas as opções de resposta.");
				return;
			}
		}

		setError(null);
		setFieldErrors({});
		setSuccess(false);
		setIsSubmitting(true);

		try {
			// Validar se já existe uma pergunta com o mesmo título
			try {
				const existingQuestions =
					await checklistQuestionsService.getAllQuestions({
						prompt: title.trim(),
						pageNumber: 1,
						pageSize: 100,
					});

				// Verifica se encontrou alguma pergunta com o mesmo título (comparação case-insensitive)
				if (
					existingQuestions.items &&
					existingQuestions.items.length > 0 &&
					existingQuestions.items.some(
						(q: ChecklistQuestion) =>
							q.prompt.toLowerCase().trim() === title.toLowerCase().trim()
					)
				) {
					setError("Essa pergunta já existe.");
					setFieldErrors({});
					return;
				}
			} catch (checkError) {
				// Se houver erro ao verificar, continua com o cadastro (o backend validará)
				console.warn("Erro ao verificar perguntas existentes:", checkError);
			}

			try {
				await createQuestionMutation.mutateAsync({
					title,
					selectedType,
					settings,
					options,
					description,
				});

				setSuccess(true);
			} catch (err: any) {
				const apiError = err?.response?.data;

				if (apiError?.error) {
					const errorMessages: string[] = [];

					let errorDetail = apiError.error.detail || apiError.error.title || "";

					// Verificar se é erro de pergunta duplicada
					const isDuplicateError =
						errorDetail.toLowerCase().includes("já existe") ||
						errorDetail.toLowerCase().includes("already exists") ||
						errorDetail.toLowerCase().includes("duplicat") ||
						errorDetail.toLowerCase().includes("pergunta já existe");

					if (isDuplicateError) {
						setError("Essa pergunta já existe.");
						setFieldErrors({});
						return;
					}

					if (
						(selectedType === 999 || selectedType === QuestionType.Option) &&
						(errorDetail.includes(
							"Uma única questão deve ser definida como padrão"
						) ||
							errorDetail.includes(
								"uma única questão deve ser definida como padrão"
							))
					) {
						errorDetail = "";
					}

					if (errorDetail) {
						errorMessages.push(errorDetail);
					}

					if (
						apiError.error.errors &&
						Array.isArray(apiError.error.errors) &&
						apiError.error.errors.length > 0
					) {
						const filteredErrors = apiError.error.errors.filter(
							(errMsg: string) => {
								const errLower = errMsg.toLowerCase();
								return !(
									errMsg.includes(
										"Uma única questão deve ser definida como padrão"
									) ||
									errMsg.includes(
										"uma única questão deve ser definida como padrão"
									) ||
									errLower.includes("já existe") ||
									errLower.includes("already exists") ||
									errLower.includes("duplicat") ||
									errLower.includes("pergunta já existe")
								);
							}
						);

						filteredErrors.forEach((errMsg: string) => {
							const normalizedErr = errMsg.trim();
							const normalizedDetail = errorDetail.trim();
							if (
								normalizedErr &&
								normalizedErr.toLowerCase() !==
									normalizedDetail.toLowerCase() &&
								normalizedErr !== normalizedDetail &&
								!normalizedDetail
									.toLowerCase()
									.includes(normalizedErr.toLowerCase()) &&
								!normalizedErr
									.toLowerCase()
									.includes(normalizedDetail.toLowerCase())
							) {
								errorMessages.push(normalizedErr);
							}
						});
					}

					const seen = new Set<string>();
					const uniqueErrors = errorMessages
						.map((msg) => msg.trim())
						.filter((msg) => {
							const lower = msg.toLowerCase();
							if (seen.has(lower)) {
								return false;
							}
							seen.add(lower);
							return true;
						});

					if (uniqueErrors.length > 0) {
						setError(uniqueErrors[0]);
					} else {
						setError(null);
					}

					if (apiError.error.fieldsErrors) {
						const filteredFieldErrors: Record<string, string[]> = {};
						Object.entries(apiError.error.fieldsErrors).forEach(
							([field, errors]) => {
								if (Array.isArray(errors)) {
									const filteredErrors = errors.filter((errMsg: string) => {
										const errLower = errMsg.toLowerCase();
										return !(
											errMsg.includes(
												"Uma única questão deve ser definida como padrão"
											) ||
											errMsg.includes(
												"uma única questão deve ser definida como padrão"
											) ||
											errLower.includes("já existe") ||
											errLower.includes("already exists") ||
											errLower.includes("duplicat") ||
											errLower.includes("pergunta já existe")
										);
									});
									if (filteredErrors.length > 0) {
										filteredFieldErrors[field] = filteredErrors;
									}
								}
							}
						);
						setFieldErrors(filteredFieldErrors);
					}
				} else {
					setError("Erro ao salvar a pergunta. Tente novamente.");
				}
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	return {
		isLoading: isSubmitting || createQuestionMutation.isPending,
		error,
		setError,
		fieldErrors,
		setFieldErrors,
		success,
		handleSave,
	};
};
